'use client'

import { useState, useEffect, useCallback } from 'react'
import Image from 'next/image'
import { resolveImageUrl } from '~/lib/images'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '~/components/ui/button'

interface EventImageCarouselProps {
  images: string[]
  alt: string
  /** Override the root sizing (default keeps the legacy 2:1 aspect). An
      explicit height in the override wins over aspect-ratio per CSS rules. */
  className?: string
}

export function EventImageCarousel({ images, alt, className = 'aspect-[2/1] max-h-[540px]' }: EventImageCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isPaused, setIsPaused] = useState(false)

  // If only one image or no images, just show it
  if (!images || images.length <= 1) {
    const imageUrl = images && images.length > 0 ? images[0] : null
    if (!imageUrl) return null

    const resolvedUrl = resolveImageUrl(imageUrl)

    return (
      <div className={`relative w-full overflow-hidden ${className}`}>
        {/* Blurred Background */}
        <div className="absolute inset-0">
          <Image
            src={resolvedUrl}
            alt=""
            fill
            className="object-cover blur-2xl scale-110 opacity-60"
            quality={30}
            sizes="100vw"
            aria-hidden="true"
          />
        </div>
        {/* Main Image */}
        <div className="absolute inset-0 flex items-center justify-center">
          <Image
            src={resolvedUrl}
            alt={alt}
            fill
            className="object-contain"
            priority
            quality={90}
            sizes="100vw"
          />
        </div>
      </div>
    )
  }

  const goToNext = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length)
    setIsPaused(true)
    setTimeout(() => setIsPaused(false), 8000)
  }, [images.length])

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length)
    setIsPaused(true)
    setTimeout(() => setIsPaused(false), 8000)
  }, [images.length])

  // Rotate images every 8 seconds with fade effect
  useEffect(() => {
    if (isPaused) return

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length)
    }, 8000)

    return () => clearInterval(interval)
  }, [images.length, isPaused])

  return (
    <div className={`relative w-full overflow-hidden group ${className}`}>
      {images.map((imageUrl, index) => {
        const resolvedUrl = resolveImageUrl(imageUrl)

        return (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentIndex ? 'opacity-100' : 'opacity-0'
            }`}
          >
            {/* Blurred Background */}
            <div className="absolute inset-0">
              <Image
                src={resolvedUrl}
                alt=""
                fill
                className="object-cover blur-2xl scale-110 opacity-60"
                quality={30}
                sizes="100vw"
                aria-hidden="true"
              />
            </div>
            {/* Main Image */}
            <div className="absolute inset-0 flex items-center justify-center">
              <Image
                src={resolvedUrl}
                alt={`${alt} - Image ${index + 1}`}
                fill
                className="object-contain"
                priority={index === 0}
                quality={90}
                sizes="100vw"
              />
            </div>
          </div>
        )
      })}

      {/* Left Navigation Button */}
      <Button
        variant="secondary"
        size="icon"
        className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={goToPrevious}
        aria-label="Previous image"
      >
        <ChevronLeft className="h-5 w-5" />
      </Button>

      {/* Right Navigation Button */}
      <Button
        variant="secondary"
        size="icon"
        className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={goToNext}
        aria-label="Next image"
      >
        <ChevronRight className="h-5 w-5" />
      </Button>

      {/* Image Indicators */}
      {images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex gap-2">
          {images.map((_, index) => (
            <button
              key={index}
              className={`h-2 rounded-full transition-all ${
                index === currentIndex
                  ? 'w-8 bg-white'
                  : 'w-2 bg-white/50 hover:bg-white/75'
              }`}
              onClick={() => {
                setCurrentIndex(index)
                setIsPaused(true)
                setTimeout(() => setIsPaused(false), 8000)
              }}
              aria-label={`Go to image ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  )
}
